/**
 * Scroll Jack Section — Frontend Logic
 *
 * Uses GSAP ScrollTrigger for pinning and scroll-driven slide transitions.
 * The section pins at a configurable offset, scroll progress maps to
 * slide transitions with snap points, and everything reverses cleanly.
 */

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

( function () {
	'use strict';

	const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	/** Parse a CSS offset value (px, vh, %) into pixels. */
	function parseOffset( raw ) {
		if ( ! raw || raw === '0' || raw === '0px' ) return 0;
		const num = parseFloat( raw );
		if ( isNaN( num ) ) return 0;
		if ( raw.includes( 'vh' ) ) return ( num / 100 ) * window.innerHeight;
		if ( raw.includes( 'vw' ) ) return ( num / 100 ) * window.innerWidth;
		if ( raw.includes( '%' ) ) return ( num / 100 ) * window.innerHeight;
		return num;
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document.querySelectorAll( '.wp-block-knightowl-blocks-scroll-section' ).forEach( initScrollSection );
	} );

	function initScrollSection( wrapper ) {
		const section = wrapper.querySelector( '.sjs' );
		const slidesWrap = wrapper.querySelector( '.sjs-slides-wrap' );
		const indicatorEl = wrapper.querySelector( '.sjs-indicator' );

		if ( ! section || ! slidesWrap ) return;

		// ── Move non-slide content above slides (pinned header area) ──
		Array.from( slidesWrap.children ).forEach( ( child ) => {
			if ( ! child.classList.contains( 'sjs-slide' ) ) {
				slidesWrap.parentNode.insertBefore( child, slidesWrap );
			}
		} );

		const slides = Array.from( slidesWrap.querySelectorAll( ':scope > .sjs-slide' ) );
		if ( slides.length === 0 ) return;

		// ── Config ──
		const runway = wrapper.querySelector( '.sjs-runway' );
		let config;
		try {
			config = JSON.parse( ( runway || section ).getAttribute( 'data-sjs-config' ) || '{}' );
		} catch { config = {}; }

		const transitionStyle = config.transition || 'fade';
		const snapEnabled = config.snap !== false;
		const distanceMultiplier = ( config.scrollDistance || 100 ) / 100;

		// ── Reduced motion — show all slides stacked ──
		if ( prefersReducedMotion ) {
			slides.forEach( ( s ) => {
				s.style.position = 'relative';
				s.style.opacity = '1';
				s.style.pointerEvents = 'auto';
			} );
			return;
		}

		// ── Single slide — no scroll-jacking needed ──
		if ( slides.length <= 1 ) {
			if ( slides[ 0 ] ) {
				slides[ 0 ].classList.add( 'sjs-active' );
				gsap.set( slides[ 0 ], { autoAlpha: 1, zIndex: 1 } );
			}
			if ( indicatorEl ) indicatorEl.style.display = 'none';
			return;
		}

		// ── State ──
		let current = 0;

		// ── Slide gap — matches WP block gap for consistent page rhythm ──
		let slideOffset = 100;
		if ( transitionStyle !== 'fade' ) {
			const gapRaw = getComputedStyle( document.documentElement ).getPropertyValue( '--wp--style--block-gap' )?.trim();
			let gapPx = 0;
			if ( gapRaw ) {
				const num = parseFloat( gapRaw );
				if ( ! isNaN( num ) ) {
					if ( gapRaw.includes( 'rem' ) ) gapPx = num * parseFloat( getComputedStyle( document.documentElement ).fontSize );
					else if ( gapRaw.includes( 'vh' ) ) gapPx = ( num / 100 ) * window.innerHeight;
					else gapPx = num;
				}
			}
			slideOffset = 100 + ( ( gapPx || 30 ) / slides[ 0 ].offsetHeight ) * 100;
		}

		// ── Initial slide setup (depends on transition style) ──
		if ( transitionStyle === 'fade' ) {
			gsap.set( slides, { autoAlpha: 0, zIndex: 0 } );
			gsap.set( slides[ 0 ], { autoAlpha: 1, zIndex: 1 } );
		} else {
			gsap.set( slides, { autoAlpha: 1, zIndex: 0 } );
			gsap.set( slides[ 0 ], { zIndex: 1 } );
			for ( let i = 1; i < slides.length; i++ ) {
				if ( transitionStyle === 'slide-up' ) {
					gsap.set( slides[ i ], { yPercent: slideOffset } );
				} else {
					gsap.set( slides[ i ], { xPercent: slideOffset } );
				}
			}
		}
		slides[ 0 ].classList.add( 'sjs-active' );

		// ── Indicator ──
		const indicatorStyle = indicatorEl?.getAttribute( 'data-style' ) || 'dots';
		buildIndicator( indicatorEl, indicatorStyle, slides );
		updateIndicator( indicatorEl, indicatorStyle, 0, slides.length );

		// ── Position indicator relative to slide ──
		const isOutside = section.classList.contains( 'sjs-indicator--outside' );
		const anchorEdge = section.classList.contains( 'sjs-indicator--anchor-edge' );
		const inner = wrapper.querySelector( '.sjs-inner' );

		/** Visible content bounds — WordPress is-layout-constrained
		 *  makes the slide element full-width while children are
		 *  narrower and centered. We need the children's rect so
		 *  the indicator aligns to visible content. */
		function contentRect( slide ) {
			const kids = slide.children;
			if ( ! kids.length ) return slide.getBoundingClientRect();
			let l = Infinity, r = -Infinity, t = Infinity, b = -Infinity;
			for ( const kid of kids ) {
				const rc = kid.getBoundingClientRect();
				if ( rc.width === 0 && rc.height === 0 ) continue;
				l = Math.min( l, rc.left );
				r = Math.max( r, rc.right );
				t = Math.min( t, rc.top );
				b = Math.max( b, rc.bottom );
			}
			if ( l === Infinity ) return slide.getBoundingClientRect();
			return { left: l, right: r, top: t, bottom: b, width: r - l, height: b - t };
		}

		function positionIndicator() {
			if ( ! indicatorEl || ! slides[ 0 ] || ! inner ) return;
			if ( ! isOutside ) return;

			const isMobile = window.innerWidth <= 782;
			const edge = isMobile
				? ( section.className.match( /sjs-indicator--mobile-(left|right|top|bottom)/ ) || [] )[ 1 ] || 'bottom'
				: ( section.className.match( /sjs-indicator--(left|right|top|bottom)\b/ ) || [] )[ 1 ] || 'right';
			const isVert = ( edge === 'left' || edge === 'right' );

			// Margins from CSS custom properties
			const scs = getComputedStyle( section );
			const sfx = isMobile ? '-m' : '';
			const mt = parseFloat( scs.getPropertyValue( '--sjs-ind-mt' + sfx ) ) || 0;
			const mr = parseFloat( scs.getPropertyValue( '--sjs-ind-mr' + sfx ) ) || 0;
			const mb = parseFloat( scs.getPropertyValue( '--sjs-ind-mb' + sfx ) ) || 0;
			const ml = parseFloat( scs.getPropertyValue( '--sjs-ind-ml' + sfx ) ) || 0;

			indicatorEl.style.margin = '0';

			const indW = indicatorEl.offsetWidth;
			const indH = indicatorEl.offsetHeight;

			// 1. Reserve padding FIRST so content shifts and there's room.
			//    anchor-to-slide: need indicator + slide-facing margin
			//    anchor-to-edge:  need edge-margin + indicator + slide-margin
			inner.style.paddingLeft = '';
			inner.style.paddingRight = '';
			inner.style.paddingTop = '';
			inner.style.paddingBottom = '';

			let pad;
			if ( anchorEdge ) {
				if ( edge === 'left' ) pad = ml + indW + mr;
				if ( edge === 'right' ) pad = mr + indW + ml;
				if ( edge === 'top' ) pad = mt + indH + mb;
				if ( edge === 'bottom' ) pad = mb + indH + mt;
			} else {
				if ( edge === 'left' ) pad = indW + mr;
				if ( edge === 'right' ) pad = indW + ml;
				if ( edge === 'top' ) pad = indH + mb;
				if ( edge === 'bottom' ) pad = indH + mt;
			}

			const cap = edge.charAt( 0 ).toUpperCase() + edge.slice( 1 );
			inner.style[ 'padding' + cap ] = pad + 'px';

			// 2. Measure AFTER padding is applied
			const cRect = contentRect( slides[ 0 ] );
			const innerRect = inner.getBoundingClientRect();
			const pos = { left: '', right: '', top: '', bottom: '' };

			// Cross-axis: align to content bounds, inset by margins
			if ( isVert ) {
				pos.top = ( cRect.top - innerRect.top + mt ) + 'px';
				pos.bottom = ( innerRect.bottom - cRect.bottom + mb ) + 'px';
			} else {
				pos.left = ( cRect.left - innerRect.left + ml ) + 'px';
				pos.right = ( innerRect.right - cRect.right + mr ) + 'px';
			}

			// Main axis
			if ( anchorEdge ) {
				if ( edge === 'left' ) pos.left = ml + 'px';
				if ( edge === 'right' ) pos.right = mr + 'px';
				if ( edge === 'top' ) pos.top = mt + 'px';
				if ( edge === 'bottom' ) pos.bottom = mb + 'px';
			} else {
				if ( edge === 'left' ) pos.left = ( cRect.left - innerRect.left - indW - mr ) + 'px';
				if ( edge === 'right' ) pos.right = ( innerRect.right - cRect.right - indW - ml ) + 'px';
				if ( edge === 'top' ) pos.top = ( cRect.top - innerRect.top - indH - mb ) + 'px';
				if ( edge === 'bottom' ) pos.bottom = ( innerRect.bottom - cRect.bottom - indH - mt ) + 'px';
			}

			indicatorEl.style.left = pos.left;
			indicatorEl.style.right = pos.right;
			indicatorEl.style.top = pos.top;
			indicatorEl.style.bottom = pos.bottom;
		}

		positionIndicator();
		// Reposition only after ScrollTrigger finishes recalculating pins on resize —
		// avoids reading stale getBoundingClientRect() values mid-refresh.
		ScrollTrigger.addEventListener( 'refresh', positionIndicator );

		// ── Accessibility ──
		section.setAttribute( 'aria-roledescription', 'carousel' );
		section.setAttribute( 'tabindex', '0' );
		slides.forEach( ( slide, i ) => {
			slide.setAttribute( 'role', 'group' );
			slide.setAttribute( 'aria-roledescription', 'slide' );
			slide.setAttribute( 'aria-label', `Slide ${ i + 1 } of ${ slides.length }` );
		} );

		// ── Build master timeline ──
		const tl = buildTimeline( slides, transitionStyle, slideOffset );

		// ── Calculate offset from CSS custom property ──
		function getOffset() {
			const isMobile = window.innerWidth <= 782;
			const prop = isMobile ? '--sjs-offset-mobile' : '--sjs-offset';
			const raw = getComputedStyle( section ).getPropertyValue( prop )?.trim() || '0px';
			return parseOffset( raw );
		}

		// ── ScrollTrigger ──
		const snapInterval = 1 / ( slides.length - 1 );

		const st = ScrollTrigger.create( {
			trigger: section,
			start: () => `top ${ getOffset() }px`,
			end: () => `+=${ ( slides.length - 1 ) * section.offsetHeight * distanceMultiplier }`,
			pin: true,
			scrub: 0.5,
			...( snapEnabled ? {
				snap: {
					snapTo: snapInterval,
					duration: { min: 0.3, max: 0.6 },
					ease: 'power3.inOut',
				},
			} : {} ),
			animation: tl,
			onUpdate: ( self ) => {
				const idx = Math.round( self.progress * ( slides.length - 1 ) );
				if ( idx !== current ) {
					slides[ current ].classList.remove( 'sjs-active' );
					slides[ current ].style.pointerEvents = 'none';
					slides[ idx ].classList.add( 'sjs-active' );
					slides[ idx ].style.pointerEvents = 'auto';
					current = idx;
					updateIndicator( indicatorEl, indicatorStyle, current, slides.length );
				}
			},
		} );

		// ── Keyboard navigation ──
		section.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowDown' || e.key === 'ArrowRight' ) {
				e.preventDefault();
				scrollToSlide( current + 1 );
			} else if ( e.key === 'ArrowUp' || e.key === 'ArrowLeft' ) {
				e.preventDefault();
				scrollToSlide( current - 1 );
			} else if ( e.key === 'Escape' ) {
				e.preventDefault();
				window.scrollTo( { top: st.end + 1, behavior: 'smooth' } );
			}
		} );

		// ── Indicator click ──
		section.addEventListener( 'sjs:goto', ( e ) => {
			const idx = e.detail?.index;
			if ( typeof idx === 'number' && idx !== current ) {
				scrollToSlide( idx );
			}
		} );

		/** Scroll to a specific slide index. */
		function scrollToSlide( idx ) {
			if ( idx < 0 || idx >= slides.length ) return;
			const progress = idx / ( slides.length - 1 );
			const target = st.start + progress * ( st.end - st.start );
			window.scrollTo( { top: target, behavior: 'smooth' } );
		}
	}

	/* ══════════════════════════════════════════════════════
	   Timeline builders — one per transition style
	   ══════════════════════════════════════════════════════ */

	function buildTimeline( slides, style, offset ) {
		const tl = gsap.timeline();

		for ( let i = 0; i < slides.length - 1; i++ ) {
			switch ( style ) {
				case 'slide-up':
					tl.to( slides[ i + 1 ], { yPercent: 0, duration: 1 }, i );
					tl.to( slides[ i ], { yPercent: -offset, duration: 1 }, i );
					break;

				case 'slide-left':
					tl.to( slides[ i + 1 ], { xPercent: 0, duration: 1 }, i );
					tl.to( slides[ i ], { xPercent: -offset, duration: 1 }, i );
					break;

				case 'fade':
				default:
					tl.to( slides[ i ], { autoAlpha: 0, duration: 1 }, i );
					tl.to( slides[ i + 1 ], { autoAlpha: 1, duration: 1 }, i );
					break;
			}
		}

		return tl;
	}

	/* ══════════════════════════════════════════════════════
	   Indicator
	   ══════════════════════════════════════════════════════ */

	function buildIndicator( el, style, slides ) {
		if ( ! el ) return;
		el.innerHTML = '';

		const click = ( node, i ) => {
			node.setAttribute( 'data-index', i );
			node.addEventListener( 'click', () => {
				el.closest( '.sjs' )?.__sjsGoTo?.( i );
			} );
		};

		switch ( style ) {
			case 'dots':
				slides.forEach( ( _, i ) => { const d = document.createElement( 'span' ); d.className = 'sjs-dot'; click( d, i ); el.appendChild( d ); } );
				break;
			case 'line':
				slides.forEach( ( _, i ) => { const seg = document.createElement( 'span' ); seg.className = 'sjs-line-seg'; click( seg, i ); el.appendChild( seg ); } );
				break;
			case 'circles':
				slides.forEach( ( s, i ) => { const c = document.createElement( 'span' ); c.className = 'sjs-circle'; c.textContent = s.getAttribute( 'data-label' ) || String( i + 1 ).padStart( 2, '0' ); click( c, i ); el.appendChild( c ); } );
				break;
			case 'numbers': {
				const n = document.createElement( 'span' ); n.className = 'sjs-number'; el.appendChild( n );
				break;
			}
			case 'dashes':
				slides.forEach( ( _, i ) => { const d = document.createElement( 'span' ); d.className = 'sjs-dash'; click( d, i ); el.appendChild( d ); } );
				break;
		}

		const sec = el.closest( '.sjs' );
		if ( sec ) {
			sec.__sjsGoTo = ( i ) => sec.dispatchEvent( new CustomEvent( 'sjs:goto', { detail: { index: i } } ) );
		}
	}

	function updateIndicator( el, style, index, total ) {
		if ( ! el ) return;
		switch ( style ) {
			case 'dots':
				el.querySelectorAll( '.sjs-dot' ).forEach( ( d, i ) => d.classList.toggle( 'sjs-active', i === index ) );
				break;
			case 'line':
				el.querySelectorAll( '.sjs-line-seg' ).forEach( ( seg, i ) => seg.classList.toggle( 'sjs-active', i === index ) );
				break;
			case 'circles':
				el.querySelectorAll( '.sjs-circle' ).forEach( ( c, i ) => c.classList.toggle( 'sjs-active', i === index ) );
				break;
			case 'numbers': {
				const n = el.querySelector( '.sjs-number' );
				if ( n ) n.textContent = `${ String( index + 1 ).padStart( 2, '0' ) } / ${ String( total ).padStart( 2, '0' ) }`;
				break;
			}
			case 'dashes':
				el.querySelectorAll( '.sjs-dash' ).forEach( ( d, i ) => d.classList.toggle( 'sjs-active', i === index ) );
				break;
		}
	}
} )();
